import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Row, Col, Input } from 'antd';
import { CloseOutlined, LeftOutlined } from '@ant-design/icons';
import { GoogleMap, Marker, InfoWindow, Autocomplete } from '@react-google-maps/api';
import { get } from 'lodash';

import PropTypes from 'prop-types';

import PdButton from 'components/pd-button';

import getAddress from 'utils/geocode';

import './style.scss';

const containerStyle = {
  width: '100%',
  height: '90%',
};

function ModalTracker({
  defaultLocation,
  mapSetter,
  onCancel,
  onOK,
  visible,
}) {
  const [map, setMap] = useState(null);
  const [markerCoordinate, setMarkerCoordinate] = useState({
    lat: -6.193066,
    lng: 106.8208469,
  });
  const [mapCenter, setMapCenter] = useState({
    lat: -6.193066,
    lng: 106.8208469,
  });
  const [markerInfo, setMarkerInfo] = useState({
    showingInfoWindow: false,
    selectedPlace: {},
  });
  const [autocompleteVal, setAutocompleteVal] = useState('');
  const [autocomplete, setAutocomplete] = useState(null);
  const searchInputRef = React.useRef(null);

  async function setterAddress() {
    let lat;
    let lng;
    if (defaultLocation.lat && defaultLocation.lng) {
      lat = defaultLocation.lat;
      lng = defaultLocation.lng;
    } else {
      lat = -6.193066;
      lng = 106.8208469;
    }
    const address = await getAddress(lat, lng);
    setMarkerCoordinate({ lat, lng, address });
    setMapCenter({ lat, lng });
    setAutocompleteVal(address);
  }

  useEffect(() => {
    if (visible) {
      setterAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (searchInputRef.current && autocompleteVal) {
      searchInputRef.current.value = autocompleteVal;
    }
  }, [autocompleteVal]);

  const onClickMap = useCallback(
    async (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const address = await getAddress(lat, lng);

      setMarkerCoordinate({ lat, lng, address });
      setAutocompleteVal(address);
    },
    []
  );

  const onClickMarker = useCallback(async () => {
    const address = await getAddress(markerCoordinate.lat, markerCoordinate.lng);
    setMarkerInfo({
      address,
      selectedPlace: markerCoordinate,
      showingInfoWindow: true,
    });
  }, [markerCoordinate]);

  const onPlaceLoaded = useCallback(
    async (place) => {
      const location = get(place, 'geometry.location', null);

      if (location !== null) {
        const lat = location.lat();
        const lng = location.lng();
        const address = await getAddress(lat, lng);
        setMarkerCoordinate({ lat, lng, address });
        setMapCenter({ lat, lng });
        setAutocompleteVal(place.formatted_address || '');

        if (map) {
          map.panTo({ lat, lng });
        }
      }
    },
    [map]
  );

  const onAutocompleteChanged = useCallback(() => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place && place.geometry) {
        onPlaceLoaded(place);
      }
    }
  }, [autocomplete, onPlaceLoaded]);

  const submitMap = () => {
    mapSetter(markerCoordinate);
    onOK();
  };

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onAutocompleteLoad = useCallback((autocomplete) => {
    setAutocomplete(autocomplete);
  }, []);

  const content = (
    <div className="select-maps">
      <Autocomplete
        onLoad={onAutocompleteLoad}
        onPlaceChanged={onAutocompleteChanged}
      >
        <Input
          ref={searchInputRef}
          className="search-location pd-margin-top-md"
          defaultValue={autocompleteVal}
          placeholder="Search location"
        />
      </Autocomplete>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={14}
        onClick={onClickMap}
        onLoad={onLoad}
      >
        <Marker
          title="Your Location"
          onClick={onClickMarker}
          position={markerCoordinate}
        />
        {markerInfo.showingInfoWindow && (
          <InfoWindow
            position={markerCoordinate}
            onCloseClick={() => {
              setMarkerInfo({
                showingInfoWindow: false,
                selectedPlace: {},
              });
            }}
          >
            <div className="info-card">
              <h3>{markerInfo.address}</h3>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      <PdButton type="primary" className="confirm-button" onClick={submitMap}>
        Save
      </PdButton>
    </div>
  );

  return (
    <>
      <Modal
        title="Location"
        className="modal--selectMaps"
        open={visible}
        onOk={onOK}
        wrapClassName="hidden-wrap-modal"
        centered
        onCancel={onCancel}>
        {content}
      </Modal>
      {visible && (
        <Row>
          <Col xs={24} lg={0}>
            <div className="pd-mobile-menu select-maps-index">
              <Row className="header">
                <Col
                  xs={{ span: 2, offset: 1 }}
                  sm={{ span: 2, offset: 1 }}
                  className="side-btn"
                  onClick={onCancel}>
                  <LeftOutlined />
                </Col>
                <Col xs={24} sm={24} className="title">
                  Pinpoint Location
                </Col>
                <Col
                  xs={{ span: 2, offset: 1 }}
                  sm={{ span: 2, offset: 1 }}
                  className="side-btn side-btn--right"
                  onClick={onCancel}>
                  <CloseOutlined />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
}

ModalTracker.propTypes = {
  defaultLocation: PropTypes.object,
  mapSetter: PropTypes.any,
  onCancel: PropTypes.any,
  onOK: PropTypes.any,
  visible: PropTypes.any,
};

export default ModalTracker;
